import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inviteTeacher, getPendingInvitations } from '../../api/teachers';
import { getCourse } from '../../api/courses';
import { extractErrorMessage } from '../../utils/api';
import { useI18n } from '../../i18n/I18nContext';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import { formatDate } from '../../utils/datetime';
import './Teachers.css';

const CheckCircleIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

function InviteTeacher() {
  const { t } = useI18n();
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [courseLoading, setCourseLoading] = useState(true);
  // courseError — set only during course-load failures; cleared at the start of every load attempt.
  // submitError — set only during invite-send failures; cleared at the start of every send attempt.
  // Keeping them separate ensures a stale course-load error never bleeds into the send-form UI
  // after a successful retry, and vice versa.
  const [courseError, setCourseError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successInfo, setSuccessInfo] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [pendingLoaded, setPendingLoaded] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [courseLoadFailed, setCourseLoadFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [canRetrySend, setCanRetrySend] = useState(false);

  const loadPending = useCallback(() => {
    let cancelled = false;
    getPendingInvitations(courseId)
      .then((res) => {
        if (!cancelled) setPendingInvitations(res || []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load pending invitations:', err);
          setPendingInvitations([]);
        }
      })
      .finally(() => {
        if (!cancelled) setPendingLoaded(true);
      });
    return () => { cancelled = true; };
  }, [courseId]);

  useEffect(() => {
    let cancelled = false;

    setCourseLoading(true);
    setCourseLoadFailed(false);
    setCourseError(''); // always clear at the start of a fresh load attempt
    getCourse(courseId)
      .then((res) => {
        if (!cancelled) setCourse(res);
      })
      .catch((err) => {
        if (!cancelled) {
          // apiClient's response interceptor reshapes axios errors into
          // { message, code, ... } — the HTTP status lives on `.code`
          // (or `.response.status` for an error that bypassed it).
          const status = err?.code ?? err?.response?.status;
          if (status === 404) {
            // Genuine not-found: leave courseLoadFailed false so the JSX
            // renders the "course not found" state instead of the
            // "check your connection, try again" retry state below.
            setCourseError(extractErrorMessage(err, t('teacherAdmin.invite.courseNotFound')));
          } else {
            setCourseError(extractErrorMessage(err, t('teacherAdmin.invite.errors.loadCourseFailed')));
            setCourseLoadFailed(true);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setCourseLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, retryTick]);

  const handleRetryLoad = useCallback(() => setRetryTick((n) => n + 1), []);

  // Fetch the pending-invitations count as soon as the course is known, so the
  // toggle button shows an accurate count before the user ever expands it.
  useEffect(() => {
    if (courseLoading) return undefined;
    return loadPending();
  }, [courseLoading, loadPending]);

  const submitInvite = async () => {
    setSubmitError(''); // clear at the start of every send attempt
    setSuccessInfo(null);
    setLinkCopied(false);
    setCanRetrySend(false);

    const trimmedEmail = email?.trim() || undefined;
    const trimmedPhone = phone?.trim() || undefined;
    if (!trimmedEmail && !trimmedPhone) {
      setSubmitError(t('teacherAdmin.invite.errors.missingContact'));
      return;
    }

    setLoading(true);

    try {
      const response = await inviteTeacher(courseId, {
        email: trimmedEmail,
        phone: trimmedPhone,
      });

      const token = response.token || response.id;
      const invitationLink =
        response.invitationLink ||
        (token ? `${window.location.origin}/invitations/accept?token=${encodeURIComponent(token)}` : '');

      setSuccessInfo({
        contact: trimmedEmail || trimmedPhone,
        link: invitationLink,
      });
      setEmail('');
      setPhone('');
      loadPending();
    } catch (err) {
      setSubmitError(extractErrorMessage(err, t('teacherAdmin.invite.errors.sendFailed')));
      setCanRetrySend(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitInvite();
  };

  const toAbsoluteLink = (link) =>
    link && !link.startsWith('http') ? `${window.location.origin}${link}` : link;

  const handleCopyLink = async () => {
    if (!successInfo?.link) return;
    const absolute = toAbsoluteLink(successInfo.link);
    try {
      await navigator.clipboard.writeText(absolute);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — the link is still visible to copy manually.
    }
  };

  if (courseLoading) {
    return (
      <div className="teachers-page">
        <h1>{t('teacherAdmin.invite.pageTitle')}</h1>
        <LoadingState skeleton="form" rows={2} />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="teachers-page">
        <h1>{t('teacherAdmin.invite.pageTitle')}</h1>
        <PageEmptyState
          variant="error"
          title={courseLoadFailed ? t('teacherAdmin.invite.courseLoadFailedTitle') : t('teacherAdmin.invite.courseNotFound')}
          description={courseLoadFailed ? (courseError || t('teacherAdmin.invite.courseLoadFailedBody')) : undefined}
          actionLabel={courseLoadFailed ? t('teacherAdmin.invite.retry') : t('teacherAdmin.invite.backToCourses')}
          onAction={courseLoadFailed ? handleRetryLoad : () => navigate('/courses')}
          secondaryLabel={courseLoadFailed ? t('teacherAdmin.invite.backToCourses') : undefined}
          onSecondaryAction={courseLoadFailed ? () => navigate('/courses') : undefined}
        />
      </div>
    );
  }

  const pendingCountLabel = pendingLoaded ? pendingInvitations.length : 0;

  return (
    <div className="teachers-page">
      <div className="teachers-header">
        <h1>{t('teacherAdmin.invite.pageTitleWithCourse', { course: course.title })}</h1>
        <button
          type="button"
          onClick={() => navigate(`/courses/${courseId}/teachers`)}
          className="teachers-back-btn"
        >
          {t('teacherAdmin.invite.viewAllTeachers')}
        </button>
      </div>

      <div className="teachers-content">
        <div className="teacher-invite-card">
          {successInfo ? (
            <div className="teacher-invite-success">
              <div className="teacher-invite-success-icon"><CheckCircleIcon /></div>
              <h2>{t('teacherAdmin.invite.successTitle')}</h2>
              <p>{t('teacherAdmin.invite.successBody', { contact: successInfo.contact, course: course.title })}</p>

              {successInfo.link && (
                <div className="teacher-invite-link-box">
                  <span className="teacher-invite-link-label">{t('teacherAdmin.invite.linkLabel')}</span>
                  <div className="teacher-invite-link-row">
                    <code className="teacher-invite-link-value">{toAbsoluteLink(successInfo.link)}</code>
                    <button type="button" onClick={handleCopyLink} className="ds-btn ds-btn-secondary ds-btn-sm">
                      <CopyIcon />
                      {linkCopied ? t('teacherAdmin.invite.linkCopied') : t('teacherAdmin.invite.copyLink')}
                    </button>
                  </div>
                  <span className="teacher-invite-link-hint">{t('teacherAdmin.invite.linkHint')}</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => setSuccessInfo(null)}
                className="ds-btn ds-btn-ghost"
              >
                {t('teacherAdmin.invite.sendAnother')}
              </button>
            </div>
          ) : (
            <>
              <h2>{t('teacherAdmin.invite.sendInvitation')}</h2>

              {submitError && (
                <div className="teachers-error" role="alert">
                  {submitError}
                  {canRetrySend && (
                    <button
                      type="button"
                      onClick={submitInvite}
                      className="ds-btn ds-btn-secondary ds-btn-sm teachers-error-retry"
                    >
                      {t('teacherAdmin.invite.retry')}
                    </button>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="teacher-form">
                <div className="teacher-form-row">
                  <div className="teacher-form-group">
                    <label htmlFor="email">{t('teacherAdmin.invite.emailAddress')}</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('teacherAdmin.invite.emailPlaceholder')}
                      disabled={loading}
                    />
                  </div>

                  <div className="teacher-form-group">
                    <label htmlFor="phone">{t('teacherAdmin.invite.phoneNumber')}</label>
                    <input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t('teacherAdmin.invite.phonePlaceholder')}
                      disabled={loading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="teachers-submit-btn"
                  disabled={loading}
                >
                  {loading ? t('teacherAdmin.invite.sendingInvitation') : t('teacherAdmin.invite.sendInvitation')}
                </button>
              </form>

              <div className="teacher-invite-info">
                <p>{t('teacherAdmin.invite.invitationInfo')}</p>
              </div>
            </>
          )}
        </div>

        <div className="teacher-invitations-card">
          <button
            type="button"
            onClick={() => setShowPending(!showPending)}
            className="teacher-toggle-pending"
            aria-expanded={showPending}
          >
            {showPending
              ? t('teacherAdmin.invite.hidePending', { count: pendingCountLabel })
              : t('teacherAdmin.invite.showPending', { count: pendingCountLabel })}
          </button>

          {showPending && (
            <div className="teacher-invitations-list">
              {!pendingLoaded ? (
                <LoadingState skeleton="list" rows={2} compact />
              ) : pendingInvitations.length === 0 ? (
                <p className="teacher-invitations-empty">{t('teacherAdmin.invite.noPendingInvitations')}</p>
              ) : (
                <table className="teachers-table">
                  <thead>
                    <tr>
                      <th>{t('teacherAdmin.invite.emailPhoneHeader')}</th>
                      <th>{t('teacherAdmin.invite.sentDateHeader')}</th>
                      <th>{t('teacherAdmin.invite.expiresHeader')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvitations.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.email || inv.phone || '-'}</td>
                        <td>
                          {inv.createdAt ? formatDate(inv.createdAt) : '—'}
                        </td>
                        <td>
                          {inv.expiresAt ? formatDate(inv.expiresAt) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InviteTeacher;
